import { useState, useEffect, useCallback, useRef } from 'react';

type NetworkState = 'online' | 'degraded' | 'offline';

interface NetworkStatus {
  state: NetworkState;
  message: string | null;
  lastChecked: Date | null;
  checkNow: () => Promise<void>;
}

const SUPABASE_URL = 'https://quidbuzjosjewriszapj.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InF1aWRidXpqb3NqZXdyaXN6YXBqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTUwNTQ2MTksImV4cCI6MjA3MDYzMDYxOX0.sULaqVfSKlgmdensRRuS2GSH4Sb5MXBEmHUitNrtI4Q';
const PROBE_INTERVAL_MS = 120_000; // 2 minutes
const PROBE_TIMEOUT_MS = 8_000;

export function useNetworkStatus(): NetworkStatus {
  const [state, setState] = useState<NetworkState>('online');
  const [message, setMessage] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const probe = useCallback(async () => {
    if (!mountedRef.current) return;

    // Skip probing when tab is hidden to avoid background request waste
    if (document.hidden) return;

    // Quick bail if browser is offline
    if (!navigator.onLine) {
      setState('offline');
      setMessage('You appear to be offline. Check your internet connection.');
      setLastChecked(new Date());
      return;
    }

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

      const res = await fetch(`${SUPABASE_URL}/rest/v1/`, {
        method: 'HEAD',
        headers: { apikey: SUPABASE_ANON_KEY },
        signal: controller.signal,
      });
      clearTimeout(timeout);

      if (!mountedRef.current) return;

      if (res.ok || res.status === 401 || res.status === 404) {
        // Any HTTP response from Supabase means connectivity is fine
        setState('online');
        setMessage(null);
      } else if (res.status >= 500) {
        setState('degraded');
        setMessage('Our servers are experiencing issues. Your requests will automatically retry.');
      } else {
        setState('online');
        setMessage(null);
      }
    } catch (err: any) {
      if (!mountedRef.current) return;

      if (err?.name === 'AbortError') {
        setState('degraded');
        setMessage('Connection is very slow. This may be due to your network\'s security settings. Try switching to mobile data or a different WiFi network.');
      } else {
        setState('offline');
        setMessage('Unable to reach our servers. Your network may be blocking this connection. Try a different network or disable your VPN.');
      }
    }

    if (mountedRef.current) {
      setLastChecked(new Date());
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    // Initial probe after short delay
    const initialTimeout = setTimeout(probe, 2000);

    // Periodic probing
    intervalRef.current = setInterval(probe, PROBE_INTERVAL_MS);

    // Browser online/offline events
    const handleOnline = () => probe();
    const handleOffline = () => {
      setState('offline');
      setMessage('You appear to be offline. Check your internet connection.');
      setLastChecked(new Date());
    };

    // Re-probe when tab becomes visible again
    const handleVisibility = () => {
      if (!document.hidden) probe();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      mountedRef.current = false;
      clearTimeout(initialTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [probe]);

  return { state, message, lastChecked, checkNow: probe };
}
